import { WorkflowRestartFailure, WorkflowRestartRequest, WorkflowRestartResponse, WorkflowRestartTarget } from '@/console/api/types'

/**
 * 워크플로 restart(LOGISPRD-15715) 화면 규칙. 서버 검증(`WorkflowRestartService.visibilityQuery`)과 같은 형식을 미리 걸러
 * 400 을 받기 전에 알려 준다. 판정 자체(대기 창 · 상한 200건)는 서버가 한다.
 */

export const RUNBOOK_URL =
  'https://github.com/musinsa/moms-be/blob/main/docs/operations/runbooks/outbound/runbook-outbound-fopt-plus-order-v2-restart-rollback.md'

/** restart 정책이 있는 타입. 서버 `FulfillmentOptimizationRestartPolicy.WORKFLOW_TYPES` 와 같다. */
export const RESTART_WORKFLOW_TYPES = [
  { value: 'FulfillmentOptimizationWorkflowV2', label: 'V2 (선할당)' },
  { value: 'FulfillmentOptimizationWorkflow', label: 'V1' },
] as const

export const MAX_RESTART_TARGETS = 200

/** runbook 6장 — 다섯 번 이어받아도 남으면 사람에게 넘긴다. */
export const MAX_RESUME_ROUNDS = 5

// 서버 WORKFLOW_ID_PREFIX_REGEX 와 같다. 따옴표·공백이 들어가면 Visibility 쿼리를 벗어난다.
const WORKFLOW_ID_PREFIX_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,199}$/

const CLOSED_AFTER_WINDOW_REASON_PREFIX = '대기 창이 닫힌 뒤 cancel 됐습니다'

export type RestartStartInput = { workflowType: string; workflowIdPrefix: string }

export type StartProblem = 'NO_CONDITION' | 'INVALID_PREFIX'

export const START_PROBLEM_MESSAGE: Record<StartProblem, string> = {
  NO_CONDITION: '워크플로 타입이나 workflowId 접두 중 하나는 있어야 해요.',
  INVALID_PREFIX: 'workflowId 접두에는 영문·숫자와 _ . : - 만 쓸 수 있어요. 공백·따옴표는 안 돼요.',
}

export const startProblemOf = ({ workflowType, workflowIdPrefix }: RestartStartInput): StartProblem | null => {
  const prefix = workflowIdPrefix.trim()
  if (!workflowType && !prefix) return 'NO_CONDITION'
  if (prefix && !WORKFLOW_ID_PREFIX_PATTERN.test(prefix)) return 'INVALID_PREFIX'
  return null
}

/** 빈 값은 싣지 않는다 — 서버는 빈 문자열과 생략을 같게 보지만 감사 로그를 읽기 쉽게 둔다. */
export const toStartRequest = ({ workflowType, workflowIdPrefix }: RestartStartInput): WorkflowRestartRequest => {
  const prefix = workflowIdPrefix.trim()
  return {
    ...(workflowType ? { workflowType } : {}),
    ...(prefix ? { workflowIdPrefix: prefix } : {}),
  }
}

export type ParsedResumeTargets = { targets: WorkflowRestartTarget[]; invalid: string[] }

/**
 * 응답을 놓쳤을 때 `[WORKFLOW-RESTART]` 로그의 `workflowId:runId` 를 붙여 넣어 이어받는다. workflowId 에도 `:` 가 올 수
 * 있어 마지막 `:` 로 나눈다(runId 는 UUID 라 `:` 가 없다). 타입은 비워 보낸다 — 서버가 실제 타입으로 덮는다.
 */
export const parseResumeTargets = (raw: string): ParsedResumeTargets => {
  const tokens = raw
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
  const invalid: string[] = []
  const targets: WorkflowRestartTarget[] = []
  tokens.forEach((token) => {
    const at = token.lastIndexOf(':')
    const workflowId = at > 0 ? token.slice(0, at) : ''
    const runId = at > 0 ? token.slice(at + 1) : ''
    if (!workflowId || !runId) {
      invalid.push(token)
      return
    }
    targets.push({ workflowId, runId, workflowType: '' })
  })
  return { targets: dedupTargets(targets), invalid }
}

export const targetKey = (target: WorkflowRestartTarget): string => `${target.workflowId}:${target.runId}`

const dedupTargets = (targets: WorkflowRestartTarget[]): WorkflowRestartTarget[] => {
  const seen = new Set<string>()
  return targets.filter((target) => {
    const key = targetKey(target)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** 창이 닫힌 뒤 cancel 된 run 은 다시 넘겨도 같은 판정이 나 사람이 할당 상태를 보고 정한다. 나머지는 일시 실패다. */
export const isClosedAfterWindow = (failure: WorkflowRestartFailure): boolean => failure.reason.startsWith(CLOSED_AFTER_WINDOW_REASON_PREFIX)

/** 이어받기로 다시 넘길 run — 아직 끝나지 않은 run 과 일시 실패한 run. */
export const resumableTargetsOf = (response: WorkflowRestartResponse): WorkflowRestartTarget[] =>
  dedupTargets([...response.pendingTargets, ...response.restartFailures.filter((f) => !isClosedAfterWindow(f)).map((f) => f.target)])

export const STATUS_COUNT_LABEL: Record<string, string> = {
  NOT_CANCELLABLE: '대기 창 밖 · 끝까지 돈다',
  UNSUPPORTED_TYPE: 'restart 대상 타입 아님',
  NOT_REACHED: '단계 상한 · 같은 조건으로 다시',
  CANCEL_REQUEST_FAILED: 'cancel 요청 실패 · 같은 조건으로 다시',
  RESUME_EXPIRED: '끝난 지 1시간 넘음 · 다시 넣지 않음',
  UNKNOWN: '상태 확인 실패',
}

/**
 * 다음에 할 일. runbook 3장 4~6단계를 응답 하나로 읽는다. 이어받기는 이어받을 run 이 있을 때만,
 * 같은 조건 재호출은 아직 cancel 하지 못한 run 이 있거나 마무리 확인이 필요할 때 권한다.
 */
export type RestartNextStep = 'RESUME' | 'CALL_AGAIN' | 'MANUAL_CHECK' | 'DONE'

export const nextStepsOf = (response: WorkflowRestartResponse, resumed: boolean): RestartNextStep[] => {
  const steps: RestartNextStep[] = []
  if (resumableTargetsOf(response).length > 0) steps.push('RESUME')
  if (response.restartFailures.some(isClosedAfterWindow)) steps.push('MANUAL_CHECK')
  // 단계 상한과 cancel 요청 실패는 run 이 Running 그대로라 같은 조건으로 다시 불러야 이어진다.
  const notReached = (response.otherStatusCounts.NOT_REACHED ?? 0) + (response.otherStatusCounts.CANCEL_REQUEST_FAILED ?? 0) > 0
  // 새로 시작한 호출이 cancel 을 하나라도 했으면 마무리 확인(같은 조건, cancelRequestedCount=0)이 남는다.
  if (!resumed && (notReached || response.cancelRequestedCount > 0)) steps.push('CALL_AGAIN')
  if (resumed && steps.length === 0) steps.push('CALL_AGAIN')
  if (steps.length === 0) steps.push('DONE')
  return steps
}
